import React from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Separator } from '@swyftx/aviary/atoms/Seperator/Seperator';
import { Tag } from '@swyftx/aviary/atoms/Tag';
import { Body, Utility } from '@swyftx/aviary/atoms/Typography';
import { useTailwindBreakpoint } from '@swyftx/aviary/hooks/useTailwindBreakpoint';
import { EnhancedTooltip } from '@swyftx/aviary/molecules/EnhancedTooltip/EnhancedTooltip';

import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { useMarginAccount } from 'src/lib/margin/hooks/account';

import { MarginInstrumentInformation } from './MarginInstrumentInformation';
import { MarginInstrumentPanelDataItem } from './MarginInstrumentPanelDataItem';
import { MarginInstrumentSelector } from './MarginInstrumentSelector';
import { MarginAccountCustomItem, MarginAccountHealthBar, MarginAccountItem } from '../../Account';

const MarginInstrumentPanel: React.FC = () => {
  const { account } = useMarginAccount();
  const isDesktopScreen = useTailwindBreakpoint('md', true);
  const { t } = useTranslation('margin');

  return (
    <div>
      <FlexLayout
        alignItems='center'
        spacing={16}
        className='w-full flex-col gap-12 px-16 md:h-full md:flex-row md:gap-16 md:px-0'
        justifyContent='space-between'
      >
        <FlexLayout
          spacing={16}
          className='h-full w-full items-start justify-between pt-8 md:w-auto md:items-center md:justify-start md:pt-0'
        >
          <MarginInstrumentSelector variant={isDesktopScreen ? 'standard' : 'mobile'} />
          <FlexLayout alignItems='center' spacing={8}>
            <MarginInstrumentInformation variant={isDesktopScreen ? 'standard' : 'mobile'} />
          </FlexLayout>
        </FlexLayout>

        <OverlayScrollbarsComponent
          className='flex w-full py-4'
          options={{ scrollbars: { visibility: 'auto', autoHide: 'leave', autoHideDelay: 400 } }}
        >
          <FlexLayout alignItems='center' justifyContent='space-between' className='h-full w-full' spacing={48}>
            <FlexLayout className='h-full' alignItems='center' spacing={16}>
              <MarginInstrumentPanelDataItem dataKey='bid' size='xlarge' />

              <Separator orientation='vertical' className='bg-color-border-main' />

              <MarginInstrumentPanelDataItem
                title={t('instruments.selector.panel.dailyChange')}
                dataKey='bidChangeYesterday'
                dynamicColor
                tooltip={t('instruments.information.dailyChange.tooltip')}
                name={t('instruments.information.dailyChange.name')}
                size='medium'
              />

              <Separator orientation='vertical' className='bg-color-border-main' />

              <MarginInstrumentPanelDataItem
                size='medium'
                title={t('instruments.selector.panel.dailyHigh')}
                tooltip={t('instruments.information.dailyHigh.tooltip')}
                name={t('instruments.information.dailyHigh.name')}
                dataKey='bidHigh24hr'
              />

              <Separator orientation='vertical' className='bg-color-border-main' />

              <MarginInstrumentPanelDataItem
                size='medium'
                title={t('instruments.selector.panel.dailyLow')}
                tooltip={t('instruments.information.dailyLow.tooltip')}
                name={t('instruments.information.dailyLow.name')}
                dataKey='bidLow24hr'
              />
            </FlexLayout>
            <FlexLayout justifyContent='end' spacing={16} className='hidden h-full md:flex'>
              {account?.leverage && (
                <MarginAccountCustomItem
                  title={t('accountOverview.leverage.title')}
                  tooltip={t('accountOverview.leverage.tooltip')}
                  size='large'
                >
                  <Tag label={`${account?.leverage}x`} />
                </MarginAccountCustomItem>
              )}

              <Separator orientation='vertical' className='bg-color-border-main' />

              <MarginAccountItem
                title={t('accountOverview.balance.title')}
                tooltip={t('accountOverview.balance.tooltip')}
                dataKey='balance'
              />

              <Separator orientation='vertical' className='bg-color-border-main' />

              <MarginAccountItem
                title={t('accountOverview.equity.title')}
                tooltip={t('accountOverview.equity.tooltip')}
                dataKey='equity'
              />

              <Separator orientation='vertical' className='bg-color-border-main' />

              <MarginAccountItem
                title={t('accountOverview.profitLoss.title')}
                tooltip={t('accountOverview.profitLoss.tooltip')}
                dataKey='profitOpen'
                dynamicColor
              />

              <Separator orientation='vertical' className='bg-color-border-main' />

              <FlexLayout direction='column' justifyContent='center' className='h-full whitespace-nowrap' spacing={4}>
                <EnhancedTooltip
                  title={t('accountOverview.marginLevel.tooltip')}
                  content={
                    <Body color='inverse' size='small' weight='emphasis'>
                      {t('accountOverview.marginLevel.content')}
                    </Body>
                  }
                  side='top'
                  align='center'
                >
                  <span>
                    <Utility variant='caption' color='secondary' wrap='none' underline='dashed'>
                      {t('accountOverview.marginLevel.title')}
                    </Utility>
                  </span>
                </EnhancedTooltip>
                <MarginAccountHealthBar variant='compact' fontSize='medium' showMarginLevel />
              </FlexLayout>
            </FlexLayout>
          </FlexLayout>
        </OverlayScrollbarsComponent>
      </FlexLayout>
    </div>
  );
};

export { MarginInstrumentPanel };
